import { autobind } from 'core-decorators'
import { inject, observer } from 'mobx-react'
import * as React from 'react'
import { Button, ButtonBar, NextButton } from '../../../../shared/components/Button'
import { ContentContainerBody, ContentRow, SplitContentContainer } from '../../../../shared/components/Content'
import { Headlines, InnerContainer, Text } from '../../../../shared/components/Elements'
import { Headline, HeadlineCSS, SubHeadline } from '../../../../shared/components/Headlines'
import { RadioButtonControl } from '../../../../shared/components/RadioButtonControl'
import { SideInformation } from '../../../../shared/components/SideInformation'
import { Jaw } from '../../../../shared/Constants'
import { breakpoint, styled } from '../../../../shared/styled-components'
import { Utils } from '../../../../shared/Utils'
import { CalculatorStoreInstance } from '../../../../stores/CalculatorStore'
import { Routes } from '../../../Routes'
import { SegmentedProgressBar } from '../result/SegmentedProgressBar'

interface IProps {
  store?: CalculatorStoreInstance
  history?: History
  jaw: Jaw
}

@inject('store')
@observer
export class BoneGraftingRequirementView extends React.Component<IProps> {
  // -------------------------------------------------------------------------- EVENTS

  @autobind
  private _onBoneGraftingChange (value: 'yes'|'no'|'maybe') {
    const { store, jaw } = this.props
    const { offer } = store
    const requirement = offer.getJawRequirement(jaw)
    requirement.setBoneGrafting(value)

    if (value === 'yes' && jaw === 'lower') {
      requirement.setBoneGraftingType('normal')
    }
  }

  // -------------------------------------------------------------------------- RENDERING

  render () {
    const { jaw, store } = this.props
    const { offer } = store!
    const requirement = offer.getJawRequirement(jaw)

    if (!requirement) return <div />

    return (<InnerContainer padded>
      <Headlines>
        <Headline centered>Knochenaufbau</Headline>
        <SubHeadline centered>Benötigen Sie einen Knochenaufbau?</SubHeadline>
      </Headlines>

      <SplitContentContainer>
        <ContentContainerBody vertical>
          <ContentRow paddedVertically>
            <Image src={Utils.getAssetPath('/assets/calculator/bone_grafting.png')} />
          </ContentRow>
          <ContentRow paddedVertically>
            <ContentText>
              Ein Knochenaufbau wird dann notwendig, wenn nicht ausreichend körpereigener Knochen vorhanden ist, um das Zahnimplantat aufzunehmen und zu stabilisieren. In der Implantologie gibt es verschiedene Techniken, durch die der Behandler ein Implantat, trotz nicht hinreichender Knochensubstanz, durch einen Knochenaufbau realisieren kann. Auf der nächsten Seite haben Sie die Möglichkeit sich tiefer über die Techniken zu informieren und die gewünschte Art des Knochenaufbaus zu wählen. Wenn Sie sich nicht sicher sind, ob Sie einen Knochenaufbau brauchen, dann wählen Sie „Vielleicht“. Wir kalkulieren in diesem Fall mit einer Minimalpauschale weiter und Ihr Behandler spricht im Zuge der persönlichen Beratung eine Empfehlung aus.
            </ContentText>
            <Options>
              <OptionHeadline>Knochenaufbau</OptionHeadline>
              <RadioButtons>
                <RadioButton
                  label='Ja'
                  checked={requirement.boneGrafting === 'yes'}
                  onChange={this._onBoneGraftingChange.bind(this, 'yes')}
                  tooltip='Sie haben bereits in einem Gespräch mit Ihrem Behandler erfahren, dass ein Knochenaufbau notwendig wird.' />
                <RadioButton
                  label='Nein'
                  checked={requirement.boneGrafting === 'no'}
                  onChange={this._onBoneGraftingChange.bind(this, 'no')}
                  tooltip='Sie haben noch genug natürliche Knochensubstanz um die Versorgung ausreichend zu stabilisieren.' />
                <RadioButton
                  label='Vielleicht'
                  checked={requirement.boneGrafting === 'maybe'}
                  onChange={this._onBoneGraftingChange.bind(this, 'maybe')}
                  tooltip='Sie sind sich nicht sicher, ob ein Knochenaufbau notwendig ist. Wir kalkulieren mit einer Minimalpauschale weiter.' />
              </RadioButtons>
            </Options>
          </ContentRow>
        </ContentContainerBody>
        <SideInformation
          onBackClick={() => store!.goBack() }
          onNextClick={() => store!.goForward()}
          infoText={jaw === 'upper'
            ? 'Titan einer der stabilsten Werkstoffe überhaupt ist und viele Vorteile bietet?'
            : 'die natürliche Knochensubstanz sich immer weiter abbaut, desto länger ein Zahnverlust besteht?'
          }
          longInfoText={jaw === 'upper'
            ? 'Titan einer der stabilsten Werkstoffe überhaupt ist und viele Vorteile bietet? Titan mit dem Reinheitsgrad 3 ist das meistverwendete Material bei Zahnimplantaten. Es ist zwischen dem Implantat (dem Imitat der natürlichen Zahnwurzel) und der Krone (dem prothetischen Zahnersatz) zu unterscheiden. Die Krone ist entweder vollkeramisch oder keramisch verblendet, während das Implantat, welches die Krone im Kiefer befestigt, aus Titan besteht und von außen nicht oder nur bedingt sichtbar ist. Titan ist sehr gut verträglich und nur wenige Patienten reagieren in Form von allergischen Reaktionen negativ auf das Material. Die außergewöhnliche Stabilität beugt Implantatbrüchen oder Verlust vor.'
            : 'die natürliche Knochensubstanz sich immer weiter abbaut, desto länger ein Zahnverlust besteht? Wenn Sie kurz nach einem Zahnverlust eine Implantatversorgung durchführen, haben Sie eine gute Chance, dass Ihr natürlicher Knochen ausreicht, um das gesetzte Implantat zu stabilisieren. Sollte das nicht der Fall sein und der Zahnverlust besteht schon länger, wird wahrscheinlich ein Knochenaufbau notwendig. Falls Sie schon ein Gespräch mit Ihrem Behandler zu diesem Thema hatten, können Sie die entsprechenden Angaben gerne machen und wir beziehen diese in die Berechnungen mit ein. Sollte dem nicht so sein, wählen Sie bitte „Vielleicht“ und sind herzlich eingeladen sich in einer unserer Partner-Praxen persönlich beraten zu lassen.'
          }/>
      </SplitContentContainer>

      <ButtonBar>
        <Button onClick={() => store!.goBack()}>Zurück</Button>
        <SegmentedProgressBar segmentCount={2} segment={1} />
        <NextButton primary onClick={() => store!.goForward()}>Weiter</NextButton>
      </ButtonBar>
    </InnerContainer>)
  }
}

const Image = styled.img`
  width: 100%;
`

const ContentText = styled(Text)`
  flex: 1;
`

const Options = styled.div`
  flex: 0;
  margin-bottom: 20px;
  ${breakpoint('tablet')`
    flex: 0 0 250px;
    padding-left: 40px;
    margin-bottom: 0;
  `}
`

const RadioButtons = styled.div`
  display: flex;
  flex-direction: column;
`

const RadioButton = styled(RadioButtonControl)`
  margin-bottom: 10px;  
  &:last-child {
    margin-bottom: 0;
  }
`

const OptionHeadline = styled.h4`
  ${HeadlineCSS};
  font-size: 20px !important;
  margin-bottom: 15px;
`
